import { useCallback, useEffect, useRef, useState } from 'react';
import { getMyProcesses } from '../../services/MySandboxService';

/**
 * The task manager's data: every process preview-runner runs in the person's own sandbox (GET /api/v1/me/processes):
 * each project's dev servers and builds, the shared datastores (state only: they serve every project, so they are
 * never stopped from Home) and the runner itself. Refreshed every `intervalMs` while the page is visible and the
 * sandbox is awake (an asleep one is never woken to be looked at). Home's "Running now" (RunningNow.jsx) shows it.
 */

/** "45 s", "12 min", "3 h 5 min", "2 d 4 h". */
export function formatSeconds(secs) {
  if (typeof secs !== 'number' || !Number.isFinite(secs)) { return '—'; }
  const s = Math.max(0, Math.round(secs));
  if (s < 60) { return `${s} s`; }
  const minutes = Math.floor(s / 60);
  if (minutes < 60) { return `${minutes} min`; }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) { return `${hours} h${minutes % 60 ? ` ${minutes % 60} min` : ''}`; }
  return `${Math.floor(hours / 24)} d${hours % 24 ? ` ${hours % 24} h` : ''}`;
}

export const MODE_LABELS = {
  spawned: null,
  external: 'External server',
  'in-process': 'Inside the preview runner',
  files: 'Files, no server',
  none: 'Starts when a project needs it',
};

/** What a datastore says next to its name: how it runs, or that it is not running. */
export function datastoreNote(store) {
  if (MODE_LABELS[store.mode]) { return MODE_LABELS[store.mode]; }
  return store.running ? null : 'Not running';
}

export function useProcesses({ enabled = true, intervalMs = 5000 } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const alive = useRef(true);
  const load = useCallback(async() => {
    try {
      const next = await getMyProcesses();
      if (alive.current) { setData(next); setError(null); setLoaded(true); }
    } catch (err) {
      if (alive.current) { setError(err); }
    }
  }, []);
  useEffect(() => {
    alive.current = true;
    if (!enabled) { return () => { alive.current = false; }; }
    load();
    const timer = setInterval(() => { if (document.visibilityState === 'visible') { load(); } }, intervalMs);
    return () => { alive.current = false; clearInterval(timer); };
  }, [enabled, intervalMs, load]);
  // loaded with no data: the sandbox answered 404, its version is older than the process list (MySandboxService).
  return { data, error, loaded, refresh: load };
}
